// CRM hygiene: labels / fix hints per check (backend keys in backend/hygiene.py CHECKS) and list filtering helpers

export const CHECK_SHORT = {
  no_venue: "Not on a venue",
  cc_channel: "CC Channel pipeline",
  won_missing_am: "No AM assigned",
  won_missing_lead_source: "No lead source",
  won_missing_contact: "No contact",
  won_future_close: "Future close date",
  quote_date_manual: "Quote date by hand",
  default_name: "Default deal name",
  duplicate: "Duplicate deals",
  cs_repeat: "Repeated CS handoff",
  venue_no_merchant: "Venue w/o merchant",
  company_no_type: "No company type",
  company_auto_created: "Auto-created company",
  rsd_rate: "RSD counted 1:1",
  won_no_line_items: "No line items",
};

export const CHECK_FIX = {
  no_venue: "Associate the deal with the Venue company (not only the Merchant) so the board counts the right location.",
  cc_channel: "The CC Channel pipeline is retired – move the deal to Direct Sales or Partnerships.",
  won_missing_am: "Fill in “AM Assigned” on every Closed Won deal.",
  won_missing_lead_source: "Fill in “Lead Source” on every Closed Won deal.",
  won_missing_contact: "Associate at least one contact with the Closed Won deal.",
  won_future_close: "A Closed Won deal cannot close in the future – set the real close date.",
  quote_date_manual: "“Quote Accepted Date” is set by the quote flow; a date before the deal was created or in the future was typed by hand.",
  default_name: "Rename the deal – “New Deal” / “Nowe Deal” is HubSpot's placeholder.",
  duplicate: "Several deals with the same name in the same pipeline on the same venue – merge or close the extra ones (CS handoff deals are not part of this check).",
  cs_repeat: "More than one CS deal created from the same WON deal – keep one CS handoff deal, close or merge the rest.",
  venue_no_merchant: "Associate the Venue with its Merchant company (company_type = Merchant) or set the Parent Company. Only venues with at least one deal (any pipeline or stage) are checked – a prospect venue without a deal is skipped until a deal is created on it.",
  company_no_type: "Set “Company type” (Merchant / Venue) on the company.",
  company_auto_created: "Merge into the manually created company (or give it a proper name).",
  rsd_rate: "The RSD amount was stored as the same number in EUR – fix the exchange rate / amount.",
  won_no_line_items: "Add the sold products as line items on the Won deal – without them the board shows the venue with no product. Covers every deal won since the cutoff, also ones created earlier.",
};

export const GROUPED_CHECKS = ["duplicate"];
// informational lists (outside the total / Δ / pill)
export const CS_GAP = "cs_gap";
export const CS_GAP_LABEL = "CS deal without source WON deal";
export const CS_GAP_HELP = "Workflow-created CS handoff deals with no deal-to-deal association to a WON deal – the repeat rule cannot check them. Not counted as a finding. Each row suggests the most likely source WON deal (same venue / name match); open the CS deal in HubSpot and add it under Associations → Deals.";
export const VENUE_FIX = "venue_fix";
export const VENUE_FIX_LABEL = "Won deal on untyped company";
export const VENUE_FIX_HELP = "Deals won since the cutoff that the board does not count because none of their companies has a Company type. Open the company in HubSpot and set Company type = Venue (or Merchant and associate its venue) – the deal appears on the board after the next refresh. Not counted as a finding (the deal is already under “Not on a venue” when created since the cutoff).";
export const PAY_GAP = "pay_provider_gap";
export const PAY_GAP_LABEL = "Payments deal without provider line item";
export const PAY_GAP_HELP = "Deals won since the cutoff whose Payments line items are only scheme fees (Transaction Fee – Visa, HR EFT POS …) and do not name the provider. The Payments breakdown shows them as “Unknown”. Add the provider line item in HubSpot – e.g. “Card Payments – Adyen (POS)”, “Card Payments – Viva (Ecom)”, “SoftPOS Fee” – and the board picks it up on the next refresh. Not counted as a finding.";
// group → info list shown next to that group's chips
export const INFO_LISTS = {
  1: { key: VENUE_FIX, label: VENUE_FIX_LABEL, help: VENUE_FIX_HELP, unit: "deals" },
  7: { key: CS_GAP, label: CS_GAP_LABEL, help: CS_GAP_HELP, unit: "CS deals" },
  11: { key: PAY_GAP, label: PAY_GAP_LABEL, help: PAY_GAP_HELP, unit: "deals" },
};
export const AUDIT_GROUPS = { 1: "Associations", 2: "Pipelines", 3: "Closed Won fields", 4: "Dates", 5: "Quotes", 6: "Naming", 7: "Duplicates", 8: "Venue ↔ Merchant", 9: "Companies", 10: "Currency", 11: "Products" };

export const slug = (s = "") => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const fmtDelta = (d) => (d === null || d === undefined ? null : d > 0 ? `+${d}` : d < 0 ? `−${Math.abs(d)}` : "±0");

export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");

// "3 × No AM assigned · 1 × Duplicate deals" – tooltip for the "fixed today" counter
export const fixedTodayText = (byCheck) => {
  const parts = Object.entries(byCheck || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${n} × ${CHECK_SHORT[k] || k}`);
  return parts.length ? `Fixed since the first check of today: ${parts.join(" · ")}` : "Fixed since the first check of today";
};

// Health of the CRM by open findings: ≥100 = poor (thermometer), ≥1000 = critical ("flatline"), 0 = clean
export const HEALTH_POOR = 100;
export const HEALTH_CRITICAL = 1000;
export function healthLevel(total, broken = false) {
  if (broken) return { level: "unknown", label: "unavailable", tone: "neutral" };
  if (total >= HEALTH_CRITICAL) return { level: "critical", label: "Critical", tone: "red", text: `Critical – ${HEALTH_CRITICAL.toLocaleString("en-US")}+ open findings, the CRM is flatlining` };
  if (total >= HEALTH_POOR) return { level: "poor", label: "Poor", tone: "amber", text: `Poor hygiene – ${HEALTH_POOR}+ open findings need fixing` };
  if (total === 0) return { level: "clean", label: "Clean", tone: "green", text: "All clean – no open findings" };
  return { level: "ok", label: "OK", tone: "neutral", text: "Under control – fewer than 100 open findings" };
}

export function filterItems(items, { check, owner, query, type }) {
  const q = (query || "").trim().toLowerCase();
  return (items || []).filter((it) => {
    if (check && !it.issues.includes(check)) return false;
    if (owner && it.owner !== owner) return false;
    if (type && it.type !== type) return false;
    if (q && ![it.name, it.venue, it.company, it.owner, it.id, it.group, it.pipeline].some((v) => v && String(v).toLowerCase().includes(q))) return false;
    return true;
  });
}

// duplicate / repeated-handoff findings are shown as groups so the team picks what to merge
export function groupItems(items) {
  const groups = new Map();
  for (const it of items) {
    const key = it.group || `single:${it.type}:${it.id}`;
    if (!groups.has(key)) groups.set(key, { key, label: it.group || null, items: [] });
    groups.get(key).items.push(it);
  }
  return [...groups.values()].sort((a, b) => b.items.length - a.items.length || (a.label || "").localeCompare(b.label || ""));
}
